import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Lock, CheckCircle2, Loader2, Eye, EyeOff, Check, X } from 'lucide-react';
import { api } from '../lib/auth';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_securedeck/artifacts/7gltxyqf_BSMS%20Rapid%20X.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password strength checks
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password),
  };
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const allChecksPass = Object.values(checks).every(Boolean);

  useEffect(() => {
    if (!token) {
      setError('No reset token provided. Please use the link from your email.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!token) {
      setError('No reset token provided');
      return;
    }

    if (!allChecksPass) {
      setError('Password does not meet all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password,
      });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, label }) => (
    <div className="flex items-center gap-2 text-xs">
      {passed ? (
        <Check className="w-3.5 h-3.5 text-emerald-400" />
      ) : (
        <X className="w-3.5 h-3.5 text-zinc-500" />
      )}
      <span className={passed ? 'text-emerald-400' : 'text-zinc-500'}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-8">
      <Card className="w-full max-w-md bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="space-y-1 text-center pb-6">
          <div className="flex justify-center mb-4">
            <img 
              src={LOGO_URL} 
              alt="BSMS Rapid X" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            RESET PASSWORD
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Create a new password for your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="p-4 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-emerald-400 font-medium">Password Reset Successful</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Your password has been updated. Redirecting to login...
                  </p>
                </div>
              </div>
              
              <Link to="/login">
                <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold">
                  GO TO LOGIN
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300 font-inter">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#09090b] border-[#3f3f46] text-white placeholder:text-zinc-600 font-mono text-sm focus:ring-1 focus:ring-blue-500"
                    required
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Password strength indicators */}
                <div className="p-3 rounded-sm bg-[#09090b] border border-[#3f3f46] space-y-1.5">
                  <p className="text-xs text-zinc-400 mb-2">Password requirements:</p>
                  <PasswordCheck passed={checks.length} label="At least 8 characters" />
                  <PasswordCheck passed={checks.uppercase} label="One uppercase letter" />
                  <PasswordCheck passed={checks.lowercase} label="One lowercase letter" />
                  <PasswordCheck passed={checks.digit} label="One number" />
                  <PasswordCheck passed={checks.special} label="One special character" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-zinc-300 font-inter">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#09090b] border-[#3f3f46] text-white placeholder:text-zinc-600 font-mono text-sm focus:ring-1 focus:ring-blue-500"
                    required
                    data-testid="reset-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPassword && (
                  <div className="flex items-center gap-2 text-xs">
                    {passwordsMatch ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <X className="w-3.5 h-3.5 text-red-400" />
                        <span className="text-red-400">Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold tracking-wide rounded-sm"
                disabled={loading || !allChecksPass || !passwordsMatch || !token}
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    RESETTING...
                  </>
                ) : (
                  'SET NEW PASSWORD'
                )}
              </Button>
              
              <div className="pt-4 border-t border-[#3f3f46] text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-zinc-400 hover:text-zinc-200"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
